/**
 * Smart Plan Expired Modal Component
 *
 * Non-dismissible modal that appears when a Smart plan user's subscription expires.
 * Informs the user their subscription has ended and they will be downgraded to Free plan.
 *
 * @module @ideoz/ui/components/molecules/SmartPlanExpiredModal
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../atoms/dialog';
import { Button } from '../atoms/button';
import { InfoSection } from '../molecules/info-section';
import { cn } from '@ideoz/ui';
import type { SmartPlanExpiredModalProps, DowngradeConfirmationDialogProps } from '../../types/SmartPlanExpiredModal.types';

/**
 * DowngradeConfirmationDialog Component
 *
 * Confirmation dialog that appears when user clicks "Proceed to downgrade"
 */
export const DowngradeConfirmationDialogComponent: React.FC<DowngradeConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  planName,
  isProcessing = false,
  className = '',
  testId = 'downgrade-confirmation-dialog',
}) => {
  const { t, i18n } = useTranslation();
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && onClose()}>
      <DialogContent
        dir={i18n.dir()}
        className={cn(
          className
        )}
        data-testid={testId}
        onInteractOutside={(e) => {
          // Prevent closing on backdrop click when processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing on Escape when processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle >
            {t('subscriptionExpired.confirmTitle')}
          </DialogTitle>
        </DialogHeader>

        <DialogDescription >
          {t('subscriptionExpired.confirmDesc', { plan: planName })}
        </DialogDescription>

        <DialogFooter >
          <Button
            variant="secondary-gray"
            onClick={onClose}
            disabled={isProcessing}
            data-testid={`${testId}-cancel-button`}
          >
            {t('subscriptionExpired.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isProcessing}
            data-testid={`${testId}-confirm-button`}
          >
            {isProcessing ? t('subscriptionExpired.processing') : t('subscriptionExpired.confirmButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SmartPlanExpiredModal Component
 *
 * Non-dismissible modal that appears when subscription expires.
 * Blocks all app interactions until user chooses to renew or downgrade.
 *
 * @component
 * @example
 * ```tsx
 * <SmartPlanExpiredModal
 *   isOpen={true}
 *   planName="Smart"
 *   onRenewSubscription={() => handleRenew()}
 *   onProceedToDowngrade={() => handleDowngrade()}
 * />
 * ```
 */
export const SmartPlanExpiredModal: React.FC<SmartPlanExpiredModalProps> = ({
  isOpen,
  planName,
  onRenewSubscription,
  onProceedToDowngrade,
  isProcessing = false,
  className = '',
  testId = 'smart-plan-expired-modal',
}) => {
  const { t, i18n } = useTranslation();
  // Handle downgrade button click - calls onProceedToDowngrade directly
  const handleProceedToDowngrade = () => {
    if (onProceedToDowngrade) {
      onProceedToDowngrade();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        dir={i18n.dir()}
        className={cn('sm:max-w-md', className)}
        data-testid={testId}
        onInteractOutside={(e) => {
          // Prevent closing on backdrop click - modal is non-dismissible
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing on Escape - modal is non-dismissible
          e.preventDefault();
        }}
      >
        {/* Header - No close button (non-dismissible) */}
        <DialogHeader>
          <DialogTitle>
            {t('subscriptionExpired.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Description */}
        <DialogDescription>
          {t('subscriptionExpired.desc')}
        </DialogDescription>

        {/* What will happen next? Section */}
        <div className="mt-2 space-y-3">
          <h3 className="text-sm font-semibold">
            {t('subscriptionExpired.whatNext')}
          </h3>
          <ul className="space-y-2 list-disc list-inside text-sm ms-2">
            <li>{t('subscriptionExpired.bulletCancelled')}</li>
            <li>{t('subscriptionExpired.bulletLoseAccess', { plan: planName })}</li>
          </ul>
        </div>

        {/* Changed your mind? Info Box */}
        <div className="mt-2">
          <InfoSection
            title={t('subscriptionExpired.changedMind')}
            description={t('subscriptionExpired.renewInfo', { plan: planName })}
            variant="info"
            data-testid={`${testId}-info-box`}
          />
        </div>

        {/* Action Buttons */}
        <DialogFooter>
          <Button
            onClick={onRenewSubscription}
            disabled={!onRenewSubscription || isProcessing}
            variant="secondary-gray"
            data-testid={`${testId}-renew-button`}
          >
            {t('subscriptionExpired.renew')}
          </Button>
          <Button
            onClick={handleProceedToDowngrade}
            disabled={isProcessing}
            variant="destructive"
            data-testid={`${testId}-downgrade-button`}
          >
            {isProcessing ? t('subscriptionExpired.processing') : t('subscriptionExpired.proceed')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

SmartPlanExpiredModal.displayName = 'SmartPlanExpiredModal';

export default SmartPlanExpiredModal;
